// ─── Portfolio Intelligence Seed Data ─────────────────────────────────────────
// Static seed data for 55 portfolio companies.
// Live API data always takes priority — this fills in static columns only.
//
// rev_trend : [$M] for 2022, 2023, 2024
// val_trend : [$B] historical — oldest to latest
// hc_trend  : [employees] 2022, 2023, 2024
// traffic_trend: [millions/month] 2022, 2023, 2024

export const PORTFOLIO_SEED = {
  "openai": {
    name: "OpenAI",
    country: "US", flag: "🇺🇸",
    sector: "AI / Foundation Models", sector_category: "AI / Tech",
    website: "openai.com",
    valuation_b: 300,
    val_trend: [29, 80, 157, 300],
    rev_trend: [200, 1600, 3700],
    hc_trend: [375, 1500, 3000], talent_signal: "Hiring ↑",
    traffic_trend: [1600, 3000, 5900],
    linkedin_followers: "3.2M",
    glassdoor_rating: 4.8,
    sentiment: "POSITIVE",
    total_raised_m: 11300, last_round: "Series F+",
    strategic_move: "Raised $40B at $300B valuation; launched GPT-4o; restructuring to for-profit",
    strategic_tag: "Fundraise",
    core_offering: "Developer of foundational AI models (GPT-4o, o3, Sora) and the ChatGPT consumer product serving over 400M weekly active users.",
    key_features: ["ChatGPT — leading AI assistant (400M+ WAU)", "GPT-4o / o3 APIs for enterprise", "DALL-E 3 image generation", "Sora video generation model", "OpenAI Enterprise contracts"],
    revenue_model: "API usage fees / ChatGPT Pro & Team subscriptions / Enterprise contracts",
    operational_structure: "HQ San Francisco CA; global offices in London, Dublin, Tokyo; ~3,000 employees",
    distribution_channels: ["API platform", "Consumer web & mobile (ChatGPT)", "Enterprise direct sales"],
    ai_capabilities: "Foundation model developer; GPT-4o, o3-mini, Sora; multimodal text/image/audio/video; proprietary training infrastructure",
  },
  "bytedance": {
    name: "ByteDance",
    country: "CN", flag: "🇨🇳",
    sector: "Social Media / AI", sector_category: "AI / Tech",
    website: "bytedance.com",
    valuation_b: 330,
    val_trend: [300, 230, 300, 330],
    rev_trend: [80000, 112000, 155000],
    hc_trend: [150000, 150000, 160000], talent_signal: "Stable →",
    traffic_trend: [2000, 2800, 3500],
    linkedin_followers: "2.1M",
    glassdoor_rating: 3.6,
    sentiment: "MIXED",
    total_raised_m: 7700, last_round: "Private",
    strategic_move: "Doubao surpassed DeepSeek in China; Q2 2025 revenue $48B (+25%); TikTok US divestiture risk",
    strategic_tag: "Revenue",
    core_offering: "Global media and technology platform operating TikTok, Douyin, and AI-powered content products across 150+ countries.",
    key_features: ["TikTok short-video platform (1B+ DAU)", "Douyin — dominant China platform", "Doubao LLM (surpassed DeepSeek in China)", "Feishu/Lark enterprise suite", "Toutiao news aggregation"],
    revenue_model: "Advertising / In-app purchases / Enterprise SaaS / E-commerce GMV share",
    operational_structure: "HQ Beijing China; global operations in 150+ countries; ~160,000 employees",
    distribution_channels: ["App stores (iOS/Android)", "Direct consumer", "Advertising platform", "API ecosystem"],
    ai_capabilities: "AI recommendation engines; Doubao foundation LLM; computer vision for content moderation; generative AI for video/image creation",
  },
  "wiz-inc": {
    name: "Wiz",
    country: "US", flag: "🇺🇸",
    sector: "Cybersecurity / Cloud", sector_category: "SaaS / B2B",
    website: "wiz.io",
    valuation_b: 32,
    val_trend: [1.7, 6, 10, 12, 32],
    rev_trend: [100, 350, 750],
    hc_trend: [750, 1500, 2900], talent_signal: "Hiring ↑",
    traffic_trend: [5, 10, 18],
    linkedin_followers: "280K",
    glassdoor_rating: 4.7,
    sentiment: "POSITIVE",
    total_raised_m: 1900, last_round: "Acquisition",
    strategic_move: "$32B Google/Alphabet acquisition agreed Mar 2025; EU approved Feb 2026; targeting $1B ARR",
    strategic_tag: "Acquisition",
    core_offering: "Agentless cloud-native security platform providing full-stack visibility and risk prioritization across multi-cloud environments.",
    key_features: ["Agentless workload protection", "Cloud Security Posture Management (CSPM)", "Risk prioritization via security graph", "Container & Kubernetes security", "AI-powered threat detection"],
    revenue_model: "SaaS ARR subscription (enterprise-tier, $750M+ ARR target)",
    operational_structure: "HQ New York NY; global R&D in Tel Aviv; offices in US/EU/APAC; ~2,900 employees",
    distribution_channels: ["Direct enterprise sales", "Cloud marketplace (AWS/Azure/GCP)", "Channel partner network"],
    ai_capabilities: "AI vulnerability prioritization; security risk graph; ML-based anomaly detection; automated remediation suggestions",
  },
  "perplexity-ai": {
    name: "Perplexity",
    country: "US", flag: "🇺🇸",
    sector: "AI Search", sector_category: "AI / Tech",
    website: "perplexity.ai",
    valuation_b: 14,
    val_trend: [1, 3, 9, 14],
    rev_trend: [30, 100, 500],
    hc_trend: [50, 150, 500], talent_signal: "Hiring ↑",
    traffic_trend: [10, 42, 110],
    linkedin_followers: "98K",
    glassdoor_rating: 4.5,
    sentiment: "POSITIVE",
    total_raised_m: 630, last_round: "Series D",
    strategic_move: "Raised $500M at $14B valuation; launched Comet browser; 780M queries/mo May 2025",
    strategic_tag: "Fundraise",
    core_offering: "AI-powered answer engine that retrieves and synthesizes real-time web information into cited, conversational responses.",
    key_features: ["Real-time web search with citations", "Perplexity Pro ($20/mo)", "Comet autonomous browser", "API for developers", "Multi-model backend (GPT, Claude, Llama)"],
    revenue_model: "Freemium / Pro subscription ($20/mo) / API usage fees / Advertising (nascent)",
    operational_structure: "HQ San Francisco CA; remote-first; ~500 employees",
    distribution_channels: ["Web app", "Mobile apps (iOS/Android)", "API platform", "Browser extension"],
    ai_capabilities: "Custom LLM pipeline with real-time web retrieval (RAG); fine-tuned models for search; multimodal query support; agentic web browsing",
  },
  "aurora-6292": {
    name: "Aurora",
    country: "US", flag: "🇺🇸",
    sector: "Autonomous Vehicles", sector_category: "AI / Tech",
    website: "aurora.tech",
    valuation_b: 3.5,
    val_trend: [13, 7, 4, 3.5],
    rev_trend: [40, 20, 50],
    hc_trend: [1900, 1700, 1600], talent_signal: "Layoffs ↓",
    traffic_trend: [3, 2.5, 2],
    linkedin_followers: "85K",
    glassdoor_rating: 3.5,
    sentiment: "CAUTIONARY",
    total_raised_m: 2400, last_round: "NASDAQ: AUR",
    strategic_move: "Launched commercial driverless trucking Texas Apr 2024; cash burn risk; layoffs Q2 2024",
    strategic_tag: "Commercial",
    core_offering: "Autonomous trucking company operating commercial driverless semi-trucks on interstate routes via the Aurora Driver platform.",
    key_features: ["Aurora Driver — Level 4 autonomy stack", "Commercial I-45 (TX) route operations", "Safety First risk management framework", "Sensor fusion (lidar + radar + camera)", "Freight logistics API for carriers"],
    revenue_model: "Per-mile transport fee / Fleet operator licensing / Technology licensing",
    operational_structure: "HQ Austin TX; Pittsburgh R&D; NASDAQ: AUR; ~1,600 employees",
    distribution_channels: ["Direct freight partnerships (FedEx, Werner)", "B2B logistics operators"],
    ai_capabilities: "Level 4 autonomous driving AI; multi-sensor fusion; real-time object detection & classification; path planning & decision algorithms",
  },
  "automation-anywhere": {
    name: "Automation Anywhere",
    country: "US", flag: "🇺🇸",
    sector: "Enterprise RPA / AI", sector_category: "SaaS / B2B",
    website: "automationanywhere.com",
    valuation_b: 5.7,
    val_trend: [5.8, 6.8, 5.1, 5.7],
    rev_trend: [520, 550, 700],
    hc_trend: [4500, 4500, 4200], talent_signal: "Stable →",
    traffic_trend: [6, 7, 8],
    linkedin_followers: "450K",
    glassdoor_rating: 3.9,
    sentiment: "NEUTRAL",
    total_raised_m: 840, last_round: "Series B",
    strategic_move: "Launched AI Agent platform; Google Cloud partnership; IPO preparation underway",
    strategic_tag: "Product",
    core_offering: "Enterprise automation platform combining RPA, AI agents, and process mining to automate complex business workflows at scale.",
    key_features: ["Automation 360 cloud-native RPA", "AI Agent Studio for agentic automation", "Process Discovery & Mining", "Document Automation (IDP)", "CoE Manager for governance"],
    revenue_model: "SaaS subscription (per bot / per user / platform tier)",
    operational_structure: "HQ San Jose CA; global offices in 40+ countries; ~4,200 employees",
    distribution_channels: ["Direct enterprise sales", "SI partner ecosystem (Accenture, Deloitte)", "Cloud marketplace (AWS/GCP/Azure)"],
    ai_capabilities: "AI agents for unstructured data; NLP for document processing; computer vision for UI automation; process mining ML; generative AI co-pilots",
  },
  "scio-technologies": {
    name: "Glean",
    country: "US", flag: "🇺🇸",
    sector: "Enterprise AI Search", sector_category: "SaaS / B2B",
    website: "glean.com",
    valuation_b: 4.6,
    val_trend: [0.5, 1, 2, 4.6],
    rev_trend: [10, 80, 200],
    hc_trend: [300, 500, 1200], talent_signal: "Hiring ↑",
    traffic_trend: [1, 2, 4],
    linkedin_followers: "120K",
    glassdoor_rating: 4.6,
    sentiment: "POSITIVE",
    total_raised_m: 600, last_round: "Series E",
    strategic_move: "Raised $260M Series E; 100+ enterprise customers; Microsoft Copilot head-to-head",
    strategic_tag: "Series E",
    core_offering: "Enterprise AI search platform that indexes all company data sources and delivers personalized search, AI answers, and intelligent work assistants.",
    key_features: ["Universal enterprise knowledge search", "AI work assistant (answer engine)", "People & expertise graph", "600+ connectors (Slack, Drive, Salesforce)", "Custom AI agents per department"],
    revenue_model: "SaaS subscription (per seat, enterprise annual contracts)",
    operational_structure: "HQ Palo Alto CA; global sales offices; ~1,200 employees",
    distribution_channels: ["Direct enterprise sales", "Integration marketplace", "Partner network"],
    ai_capabilities: "Retrieval-augmented generation (RAG); semantic search; personalization ML; LLM-powered Q&A over company knowledge",
  },
  "sierra-1124": {
    name: "Sierra",
    country: "US", flag: "🇺🇸",
    sector: "Conversational AI", sector_category: "AI / Tech",
    website: "sierra.ai",
    valuation_b: 4.5,
    val_trend: [0.2, 0.8, 4.5],
    rev_trend: [20, 80, 200],
    hc_trend: [100, 200, 500], talent_signal: "Hiring ↑",
    traffic_trend: [0.5, 1, 2],
    linkedin_followers: "35K",
    glassdoor_rating: 4.7,
    sentiment: "POSITIVE",
    total_raised_m: 200, last_round: "Series B",
    strategic_move: "Series B $175M; deployed at Sirius XM, WeightWatchers; co-founded by ex-Salesforce CEO",
    strategic_tag: "Series B",
    core_offering: "Enterprise conversational AI platform enabling brands to deploy intelligent, brand-aware AI agents for customer support and engagement.",
    key_features: ["No-code AI agent builder", "Brand-voice trained AI", "Multi-channel deployment (web, mobile, phone)", "Customer analytics dashboard", "Secure enterprise integrations"],
    revenue_model: "SaaS subscription (enterprise contracts, seat-based)",
    operational_structure: "HQ San Francisco CA; lean team ~500; remote-first culture",
    distribution_channels: ["Direct enterprise sales", "Salesforce ecosystem partnerships"],
    ai_capabilities: "Custom fine-tuned LLMs for brand voice; multi-turn conversation management; real-time knowledge retrieval; tone & policy enforcement layers",
  },
  "recursion-pharmaceuticals": {
    name: "Recursion Pharmaceuticals",
    country: "US", flag: "🇺🇸",
    sector: "AI Drug Discovery", sector_category: "Biotech / Health",
    website: "recursion.com",
    valuation_b: 2.1,
    val_trend: [5, 4, 3, 2.5, 2.1],
    rev_trend: [193, 260, 340],
    hc_trend: [1600, 1800, 1900], talent_signal: "Stable →",
    traffic_trend: [1.2, 1.4, 1.5],
    linkedin_followers: "95K",
    glassdoor_rating: 4.0,
    sentiment: "CAUTIONARY",
    total_raised_m: 800, last_round: "NASDAQ: RXRX",
    strategic_move: "Roche partnership extended; NASDAQ: RXRX; pipeline advancing but cash burn concern",
    strategic_tag: "Partnership",
    core_offering: "AI-driven drug discovery platform using industrial-scale biology imaging and machine learning to map cellular biology and identify novel therapeutics.",
    key_features: ["Recursion OS — proprietary biology OS", "55+ petabytes of biological images", "BioHive-2 supercomputer (NVIDIA)", "20+ active drug programs", "Phenom1 foundation biology model"],
    revenue_model: "Pharmaceutical licensing deals / Research partnerships / NASDAQ: RXRX equity",
    operational_structure: "HQ Salt Lake City UT; NASDAQ: RXRX; ~1,900 employees",
    distribution_channels: ["Pharma partnership licensing", "Academic research collaborations", "Public markets"],
    ai_capabilities: "Computer vision for high-throughput phenomics; ML drug-target identification; foundation models for biology; causal AI for mechanism-of-action",
  },
  "quera-computing": {
    name: "QuEra Computing",
    country: "US", flag: "🇺🇸",
    sector: "Quantum Computing", sector_category: "Deep Tech",
    website: "quera.com",
    valuation_b: 0.6,
    val_trend: [0.2, 0.4, 0.6],
    rev_trend: [5, 12, 30],
    hc_trend: [60, 80, 120], talent_signal: "Hiring ↑",
    traffic_trend: [0.1, 0.2, 0.3],
    linkedin_followers: "18K",
    glassdoor_rating: 4.2,
    sentiment: "NEUTRAL",
    total_raised_m: 230, last_round: "Series A",
    strategic_move: "256-qubit neutral-atom computer; DARPA contract; Harvard spinout with quantum advantage claims",
    strategic_tag: "Research",
    core_offering: "Neutral-atom quantum computing company offering cloud access to 256-qubit processors with demonstrated logical qubit capabilities.",
    key_features: ["256-qubit neutral-atom quantum processor", "Cloud quantum access (AWS Braket)", "Error-corrected logical qubits", "DARPA & IARPA government contracts", "Quantum algorithms & tooling support"],
    revenue_model: "Cloud compute access fees / Government research contracts / Academic partnerships",
    operational_structure: "HQ Boston MA (Harvard spinout); ~120 employees; university research collaboration model",
    distribution_channels: ["Cloud API (AWS Braket integration)", "Government contract awards", "Academic & enterprise research partnerships"],
    ai_capabilities: "Quantum machine learning algorithms; quantum optimization; hybrid classical-quantum computation; quantum simulation for chemistry & materials",
  },
  "helion-energy": {
    name: "Helion Energy",
    country: "US", flag: "🇺🇸",
    sector: "Fusion Energy", sector_category: "Deep Tech",
    website: "helionenergy.com",
    valuation_b: 3.8,
    val_trend: [0.4, 0.4, 3.8, 3.8],
    rev_trend: [2, 5, 20],
    hc_trend: [200, 250, 300], talent_signal: "Stable →",
    traffic_trend: [0.3, 0.4, 0.5],
    linkedin_followers: "45K",
    glassdoor_rating: 4.4,
    sentiment: "NEUTRAL",
    total_raised_m: 575, last_round: "Series F",
    strategic_move: "Microsoft PPA signed for 2028 delivery; 7th prototype Trenta operational; $500M Series F",
    strategic_tag: "Series F",
    core_offering: "Fusion energy company developing pulsed field-reversed configuration reactors targeting commercial electricity generation without radioactive waste.",
    key_features: ["Trenta — 7th generation fusion prototype", "Microsoft PPA for 2028 power delivery", "Direct electricity generation design", "No radioactive waste by-product", "Proprietary pulsed FRC technology"],
    revenue_model: "Power purchase agreements (PPA) / Technology licensing (post-commercial)",
    operational_structure: "HQ Everett WA; ~300 employees; private pre-revenue company",
    distribution_channels: ["Utility PPA (Microsoft anchor customer)", "Future grid operator partnerships"],
    ai_capabilities: "ML for plasma physics optimization; predictive modeling for reactor control; simulation AI for fusion parameter tuning",
  },
  "umoja-biopharma": {
    name: "Umoja Biopharma",
    country: "US", flag: "🇺🇸",
    sector: "Cell Therapy / Oncology", sector_category: "Biotech / Health",
    website: "umojabiopharma.com",
    valuation_b: 1.2,
    val_trend: [0.5, 0.9, 1.2],
    rev_trend: [10, 20, 40],
    hc_trend: [150, 180, 220], talent_signal: "Stable →",
    traffic_trend: [0.2, 0.3, 0.4],
    linkedin_followers: "12K",
    glassdoor_rating: 4.0,
    sentiment: "CAUTIONARY",
    total_raised_m: 200, last_round: "Series C",
    strategic_move: "IND filing for lead in vivo CAR-T asset; Series C closed; clinical readout expected 2025",
    strategic_tag: "Clinical",
    core_offering: "In vivo CAR-T cell therapy company developing lentiviral-delivered programmable RNA treatments to fight cancer without patient cell extraction.",
    key_features: ["In vivo CAR-T (no apheresis needed)", "Proprietary VivoVec lentiviral platform", "CD19 and CD22 cancer targeting", "Off-the-shelf single-dose design", "IND filing for lead oncology asset"],
    revenue_model: "Pre-revenue (clinical stage) / Pharma licensing / Partnership milestones",
    operational_structure: "HQ Seattle WA; ~220 employees; clinical-stage biotech",
    distribution_channels: ["Clinical trial sites", "Pharma licensing partnerships"],
    ai_capabilities: "ML for lentiviral vector design optimization; bioinformatics for target identification; predictive safety modeling",
  },
  "metsera": {
    name: "Metsera",
    country: "US", flag: "🇺🇸",
    sector: "GLP-1 / Obesity Biotech", sector_category: "Biotech / Health",
    website: "metsera.com",
    valuation_b: 2.4,
    val_trend: [0.3, 0.6, 2.4],
    rev_trend: [5, 15, 40],
    hc_trend: [30, 60, 120], talent_signal: "Hiring ↑",
    traffic_trend: [0.2, 0.5, 0.8],
    linkedin_followers: "8K",
    glassdoor_rating: 4.1,
    sentiment: "POSITIVE",
    total_raised_m: 320, last_round: "Series B",
    strategic_move: "Phase 1 positive readout; oral GLP-1 candidate advancing; Series B $290M; GLP-1 market surging",
    strategic_tag: "Phase 1",
    core_offering: "Next-generation GLP-1 biotech developing oral and injectable obesity and metabolic disease therapies with improved tolerability profiles.",
    key_features: ["Oral GLP-1 candidate (once-weekly)", "Phase 1 positive clinical readout", "Next-gen peptide engineering platform", "Targeting obesity + metabolic syndrome", "Differentiated vs. Ozempic/Wegovy"],
    revenue_model: "Pre-revenue (clinical stage) / Pharma licensing / Partnership deals",
    operational_structure: "HQ New York NY; ~120 employees; virtual biotech model",
    distribution_channels: ["Pharma licensing", "Clinical trial partnerships"],
    ai_capabilities: "AI-assisted peptide optimization; ML for ADME property prediction; structure-activity relationship modeling; computational chemistry",
  },
  "doordash": {
    name: "DoorDash",
    country: "US", flag: "🇺🇸",
    sector: "Food Delivery", sector_category: "E-Commerce",
    website: "doordash.com",
    valuation_b: 22,
    val_trend: [18, 18, 20, 22],
    rev_trend: [6583, 8060, 10700],
    hc_trend: [43000, 37000, 38000], talent_signal: "Stable →",
    traffic_trend: [180, 200, 250],
    linkedin_followers: "620K",
    glassdoor_rating: 3.7,
    sentiment: "POSITIVE",
    total_raised_m: 2500, last_round: "NASDAQ: DASH",
    strategic_move: "Japan expansion; DashPass 18M subscribers; grocery AI agent inside ChatGPT Dec 2025",
    strategic_tag: "Expansion",
    core_offering: "On-demand food, grocery, and retail delivery platform connecting consumers with 37M+ merchants via Dashers in 27+ countries.",
    key_features: ["Food & grocery delivery (27 countries)", "DashPass loyalty program (18M subscribers)", "DoorDash Drive white-label logistics", "Wolt acquisition (European expansion)", "DoorDash Ads merchant platform"],
    revenue_model: "Commission fees / DashPass subscriptions ($9.99/mo) / Advertising / White-label logistics fees",
    operational_structure: "HQ San Francisco CA; NASDAQ: DASH; global operations in US/EU/APAC; ~38,000 employees",
    distribution_channels: ["Consumer mobile app", "DoorDash Drive API (enterprise)", "Merchant partnership portal"],
    ai_capabilities: "ML for order routing & dispatch optimization; demand forecasting; dynamic pricing; AI customer service agents; route optimization",
  },
  "coupang": {
    name: "Coupang",
    country: "KR", flag: "🇰🇷",
    sector: "E-Commerce", sector_category: "E-Commerce",
    website: "coupang.com",
    valuation_b: 25,
    val_trend: [84, 25, 21, 25],
    rev_trend: [20583, 24400, 30000],
    hc_trend: [60000, 65000, 69000], talent_signal: "Stable →",
    traffic_trend: [290, 330, 380],
    linkedin_followers: "310K",
    glassdoor_rating: 3.5,
    sentiment: "POSITIVE",
    total_raised_m: 3900, last_round: "NYSE: CPNG",
    strategic_move: "Taiwan Rocket Delivery launched; Farfetch acquisition; NYSE: CPNG revenue +23% YoY",
    strategic_tag: "Expansion",
    core_offering: "South Korea's dominant e-commerce and Rocket Delivery platform operating a vertically integrated supply chain with same-day delivery.",
    key_features: ["Rocket Delivery — next-day/same-day guaranteed", "Rocket Fresh grocery delivery", "Coupang Eats food delivery", "Farfetch luxury fashion (acquired)", "Coupang Play streaming service"],
    revenue_model: "1P product sales / 3P marketplace fees / Rocket Wow subscription (KRW 4,990/mo) / Advertising",
    operational_structure: "HQ Seoul KR; NYSE: CPNG; expanding to Taiwan; ~69,000 employees",
    distribution_channels: ["Direct consumer app", "Own fulfillment & delivery network", "Third-party merchant marketplace"],
    ai_capabilities: "ML demand forecasting; inventory optimization; personalized recommendations; search ranking; delivery route optimization",
  },
  "flipkart": {
    name: "Flipkart",
    country: "IN", flag: "🇮🇳",
    sector: "E-Commerce", sector_category: "E-Commerce",
    website: "flipkart.com",
    valuation_b: 35,
    val_trend: [37, 35, 35, 35],
    rev_trend: [9000, 10000, 12000],
    hc_trend: [63000, 65000, 70000], talent_signal: "Stable →",
    traffic_trend: [120, 140, 163],
    linkedin_followers: "1.8M",
    glassdoor_rating: 3.8,
    sentiment: "POSITIVE",
    total_raised_m: 12600, last_round: "Pre-IPO",
    strategic_move: "Shopsy crossed 100M users; IPO preparation underway; Walmart-backed; strong India dominance",
    strategic_tag: "IPO Prep",
    core_offering: "India's largest e-commerce marketplace offering 500M+ products across categories, backed by Walmart and preparing for domestic IPO.",
    key_features: ["Marketplace platform (500M+ SKUs)", "Flipkart Plus loyalty rewards", "Shopsy social commerce (100M+ users)", "Flipkart Health+ pharmacy", "Ekart logistics network"],
    revenue_model: "Commission fees / Advertising / Logistics / Financial services (BNPL)",
    operational_structure: "HQ Bengaluru India; Walmart majority-owned (~75%); ~70,000 employees",
    distribution_channels: ["Consumer app/web (India-first)", "Shopsy social commerce app", "Seller marketplace portal"],
    ai_capabilities: "AI-powered search & discovery; visual search; personalization engine; fraud detection; delivery route & demand optimization",
  },
  "tokopedia": {
    name: "Tokopedia",
    country: "ID", flag: "🇮🇩",
    sector: "E-Commerce / Marketplace", sector_category: "E-Commerce",
    website: "tokopedia.com",
    valuation_b: 2.8,
    val_trend: [14, 3, 2.8],
    rev_trend: [2000, 2800, 3100],
    hc_trend: [6000, 5000, 4500], talent_signal: "Layoffs ↓",
    traffic_trend: [75, 85, 95],
    linkedin_followers: "680K",
    glassdoor_rating: 3.9,
    sentiment: "MIXED",
    total_raised_m: 3000, last_round: "Strategic",
    strategic_move: "Merged with TikTok Shop Indonesia; rebranded to TikTok Shop; restructuring underway",
    strategic_tag: "M&A",
    core_offering: "Indonesia's leading e-commerce marketplace, now operating under TikTok Shop partnership after a landmark merger with ByteDance.",
    key_features: ["Marketplace platform (Indonesia's #1)", "TikTok Shop live commerce integration", "Tokofinancial BNPL & payments", "Tokopedia seller tools", "Logistics & fulfillment network"],
    revenue_model: "Commission fees / Advertising / Financial services / TikTok Shop revenue share",
    operational_structure: "HQ Jakarta Indonesia; ~4,500 employees (post-restructuring); TikTok Shop integration ongoing",
    distribution_channels: ["Consumer app/web (Indonesia-first)", "TikTok in-app shopping", "Seller marketplace portal"],
    ai_capabilities: "AI fraud detection; personalized recommendations; social commerce AI; logistics route optimization; search ranking",
  },
  "oyo-rooms": {
    name: "OYO",
    country: "IN", flag: "🇮🇳",
    sector: "Hospitality Tech", sector_category: "E-Commerce",
    website: "oyorooms.com",
    valuation_b: 2.5,
    val_trend: [10, 9, 4, 2.7, 2.5],
    rev_trend: [400, 550, 700],
    hc_trend: [10000, 3500, 3200], talent_signal: "Layoffs ↓",
    traffic_trend: [18, 20, 22],
    linkedin_followers: "450K",
    glassdoor_rating: 3.2,
    sentiment: "CAUTIONARY",
    total_raised_m: 3200, last_round: "Pre-IPO",
    strategic_move: "Debt restructuring complete; refiled DRHP for India IPO; Ritesh Agarwal buyback complete",
    strategic_tag: "IPO Prep",
    core_offering: "Hospitality technology platform franchising and managing budget-to-midscale hotels in 35+ countries via AI-powered revenue management.",
    key_features: ["OYO branded hotel network (40,000+ hotels)", "Townhouse premium segment", "OYO Vacation Homes", "AI revenue management system", "Partner hotel transformation program"],
    revenue_model: "Revenue share from hotel partners / SaaS platform fees / Direct bookings commission",
    operational_structure: "HQ Gurugram India; operations in 35+ countries; ~3,200 employees (post-restructuring); pre-IPO",
    distribution_channels: ["Direct app/web booking", "OTA partnerships (MakeMyTrip, Booking.com)", "Corporate travel sales"],
    ai_capabilities: "AI dynamic pricing & revenue management; demand forecasting; automated guest communication; housekeeping optimization",
  },
  "c2fo": {
    name: "C2FO",
    country: "US", flag: "🇺🇸",
    sector: "Working Capital / Fintech", sector_category: "Fintech",
    website: "c2fo.com",
    valuation_b: 1.5,
    val_trend: [1.5, 1.5, 1.5, 1.5],
    rev_trend: [180, 200, 240],
    hc_trend: [850, 900, 950], talent_signal: "Stable →",
    traffic_trend: [2, 2.5, 3],
    linkedin_followers: "55K",
    glassdoor_rating: 3.7,
    sentiment: "NEUTRAL",
    total_raised_m: 430, last_round: "Series F",
    strategic_move: "Crossed $2T in marketplace transactions; launched AI-powered dynamic discounting feature",
    strategic_tag: "Product",
    core_offering: "Working capital marketplace connecting suppliers and buyers to enable early payment at dynamic discount rates, having processed $2T+ in transactions.",
    key_features: ["Early payment marketplace ($2T+ processed)", "AI-powered dynamic discounting", "Supply chain finance automation", "Real-time cash flow management portal", "Global bank & enterprise partnerships"],
    revenue_model: "Transaction fee (yield on discount) / SaaS licensing to enterprise buyers",
    operational_structure: "HQ Leawood KS; global operations (US/EU/Asia); ~950 employees",
    distribution_channels: ["Direct enterprise sales to large buyers", "ERP/procurement system integrations", "Bank and financial institution partnerships"],
    ai_capabilities: "ML for dynamic discount rate pricing; predictive cash flow analytics; counterparty risk scoring; fraud detection",
  },
  "travelperk": {
    name: "TravelPerk",
    country: "ES", flag: "🇪🇸",
    sector: "Corporate Travel SaaS", sector_category: "SaaS / B2B",
    website: "travelperk.com",
    valuation_b: 1.4,
    val_trend: [1.3, 1.3, 1.4],
    rev_trend: [70, 110, 190],
    hc_trend: [900, 1000, 1300], talent_signal: "Hiring ↑",
    traffic_trend: [3, 4.5, 6],
    linkedin_followers: "120K",
    glassdoor_rating: 4.4,
    sentiment: "POSITIVE",
    total_raised_m: 450, last_round: "Series D",
    strategic_move: "Acquired AmTrav (US expansion); launched AI travel agent; Series D closed; strong SMB growth",
    strategic_tag: "M&A",
    core_offering: "All-in-one corporate travel management platform with integrated booking, expense management, FlexiPerk cancellation, and an AI travel agent.",
    key_features: ["Global travel booking (flights, hotels, rail)", "FlexiPerk flexible cancellation guarantee", "Integrated expense management", "Carbon footprint tracking & offset", "AI travel agent (autonomous booking)"],
    revenue_model: "SaaS subscription / Transaction fee / FlexiPerk premium add-on",
    operational_structure: "HQ Barcelona Spain; US offices (NYC, SF post-AmTrav); ~1,300 employees",
    distribution_channels: ["Direct SMB/mid-market sales", "Self-serve online onboarding", "Travel agent & TMC partnerships"],
    ai_capabilities: "AI travel policy compliance enforcement; autonomous trip booking agent; expense categorization ML; predictive cost optimization",
  },
  "advance-intelligence-group": {
    name: "Advance Intelligence Group",
    country: "SG", flag: "🇸🇬",
    sector: "Fintech / SEA", sector_category: "Fintech",
    website: "advance-ai.com",
    valuation_b: 0.9,
    val_trend: [2.0, 1.1, 0.9],
    rev_trend: [65, 90, 115],
    hc_trend: [1100, 1000, 950], talent_signal: "Stable →",
    traffic_trend: [1.5, 1.8, 2],
    linkedin_followers: "35K",
    glassdoor_rating: 3.5,
    sentiment: "CAUTIONARY",
    total_raised_m: 430, last_round: "Series D",
    strategic_move: "Launched Ginee merchant SaaS; expanded to Philippines; SEA macro headwinds weigh on fintech",
    strategic_tag: "Expansion",
    core_offering: "Southeast Asia fintech and merchant services platform offering BNPL (Atome), AI credit scoring, and merchant SaaS (Ginee) across 10+ markets.",
    key_features: ["Atome BNPL platform (10+ SEA markets)", "Advance AI credit scoring for underbanked", "Ginee merchant management SaaS", "B2B supply chain financing", "Philippines & Vietnam expansion"],
    revenue_model: "BNPL merchant fees / SaaS subscriptions / Credit interest / Transaction fees",
    operational_structure: "HQ Singapore; operations in 10+ SEA markets; ~950 employees",
    distribution_channels: ["Direct consumer app (Atome)", "Merchant platform (Ginee)", "Bank & fintech partnerships"],
    ai_capabilities: "AI credit scoring for underbanked consumers; alternative data ML models; fraud detection; behavioral risk modeling",
  },
  "6sense": {
    name: "6sense",
    country: "US", flag: "🇺🇸",
    sector: "B2B Revenue Intelligence", sector_category: "SaaS / B2B",
    website: "6sense.com",
    valuation_b: 5.2,
    val_trend: [3.5, 5, 5.2],
    rev_trend: [100, 150, 200],
    hc_trend: [1200, 1400, 1500], talent_signal: "Stable →",
    traffic_trend: [2.5, 3.2, 4],
    linkedin_followers: "95K",
    glassdoor_rating: 4.1,
    sentiment: "POSITIVE",
    total_raised_m: 430, last_round: "Series F",
    strategic_move: "Launched Revenue AI; CEO transition; $200M ARR crossed; Series F at $5.2B valuation",
    strategic_tag: "Revenue",
    core_offering: "B2B revenue AI platform that identifies in-market buyers using intent signals and account-based data to accelerate pipeline generation.",
    key_features: ["Intent data & buyer identification", "Predictive account engagement scoring", "Revenue AI for sales & marketing", "Dark funnel visibility", "CRM/MAP integrations (Salesforce, HubSpot)"],
    revenue_model: "SaaS subscription (per seat + platform tier)",
    operational_structure: "HQ San Francisco CA; global sales force; ~1,500 employees",
    distribution_channels: ["Direct enterprise sales", "Integration with Salesforce/HubSpot/Marketo", "Partner ecosystem"],
    ai_capabilities: "Predictive intent modeling; dark funnel identification; NLP for buyer journey analysis; AI revenue forecasting; account prioritization ML",
  },
  "tines": {
    name: "Tines",
    country: "IE", flag: "🇮🇪",
    sector: "Security Automation", sector_category: "SaaS / B2B",
    website: "tines.com",
    valuation_b: 0.5,
    val_trend: [0.3, 0.5, 0.5],
    rev_trend: [15, 35, 65],
    hc_trend: [150, 200, 280], talent_signal: "Hiring ↑",
    traffic_trend: [0.8, 1.1, 1.5],
    linkedin_followers: "28K",
    glassdoor_rating: 4.6,
    sentiment: "POSITIVE",
    total_raised_m: 115, last_round: "Series C",
    strategic_move: "Crossed $50M ARR; AI-native workflow expansion; security automation leader in EMEA",
    strategic_tag: "Growth",
    core_offering: "No-code security automation platform enabling security teams to build sophisticated playbooks and workflows without engineering dependencies.",
    key_features: ["Drag-and-drop workflow builder", "500+ pre-built security integrations", "AI-assisted playbook generation", "Hosted + self-hosted deployment options", "SOC workflow library (100+ templates)"],
    revenue_model: "SaaS subscription (seat-based, enterprise annual contracts)",
    operational_structure: "HQ Dublin Ireland; US offices in NYC & San Francisco; ~280 employees",
    distribution_channels: ["Direct enterprise sales (EMEA + US)", "Security integration partner channel", "MSSP partnerships"],
    ai_capabilities: "AI workflow suggestion engine; NLP for alert triage; LLM-assisted playbook builder; anomaly detection in security telemetry",
  },
  "adverity": {
    name: "Adverity",
    country: "AT", flag: "🇦🇹",
    sector: "Marketing Data Platform", sector_category: "SaaS / B2B",
    website: "adverity.com",
    valuation_b: 0.35,
    val_trend: [0.3, 0.35, 0.35],
    rev_trend: [25, 35, 50],
    hc_trend: [280, 300, 320], talent_signal: "Stable →",
    traffic_trend: [0.8, 1, 1.2],
    linkedin_followers: "22K",
    glassdoor_rating: 4.3,
    sentiment: "NEUTRAL",
    total_raised_m: 90, last_round: "Series E",
    strategic_move: "AI data harmonization launched; US GTM expansion; Series E; marketing analytics leader EU",
    strategic_tag: "Product",
    core_offering: "Marketing data integration and analytics platform that unifies data from 600+ sources for marketing performance insights and reporting.",
    key_features: ["600+ data connectors", "Automated data harmonization & mapping", "AI-powered data quality monitoring", "Marketing performance dashboards", "Predictive campaign analytics"],
    revenue_model: "SaaS subscription (usage-based + seat-based tiers)",
    operational_structure: "HQ Vienna Austria; US office (NYC); UK office; ~320 employees",
    distribution_channels: ["Direct enterprise sales", "Agency partnerships", "Self-service SMB onboarding"],
    ai_capabilities: "AI schema mapping and harmonization; anomaly detection in marketing data; automated insight generation; NLP query for data exploration",
  },
  "360learning": {
    name: "360Learning",
    country: "US", flag: "🇺🇸",
    sector: "Learning Management SaaS", sector_category: "SaaS / B2B",
    website: "360learning.com",
    valuation_b: 0.48,
    val_trend: [0.3, 0.48, 0.48],
    rev_trend: [32, 45, 60],
    hc_trend: [380, 400, 430], talent_signal: "Stable →",
    traffic_trend: [1.2, 1.5, 1.8],
    linkedin_followers: "42K",
    glassdoor_rating: 4.2,
    sentiment: "NEUTRAL",
    total_raised_m: 220, last_round: "Series B",
    strategic_move: "AI course builder launched; 2,000+ enterprise customers; collaborative LMS differentiation",
    strategic_tag: "Product",
    core_offering: "Collaborative learning management system enabling companies to build and share internal courses using subject-matter expert peer knowledge.",
    key_features: ["Collaborative course creation tools", "AI course builder from documents", "Upskilling path recommendations", "Learner engagement analytics", "API-first LMS architecture"],
    revenue_model: "SaaS subscription (per active user / per seat)",
    operational_structure: "HQ New York NY + Paris France (bilingual HQ); ~430 employees",
    distribution_channels: ["Direct mid-market & enterprise sales", "HRIS/LMS integrations (Workday, SAP)"],
    ai_capabilities: "AI course generation from internal documentation; adaptive learning path ML; skills gap analysis; engagement prediction modeling",
  },
  "10x-genomics": {
    name: "10X Genomics",
    country: "US", flag: "🇺🇸",
    sector: "Genomics Technology", sector_category: "Biotech / Health",
    website: "10xgenomics.com",
    valuation_b: 1.4,
    val_trend: [20, 8, 4, 2, 1.4],
    rev_trend: [488, 524, 612],
    hc_trend: [1400, 1400, 1380], talent_signal: "Stable →",
    traffic_trend: [2, 2.5, 3],
    linkedin_followers: "120K",
    glassdoor_rating: 3.8,
    sentiment: "CAUTIONARY",
    total_raised_m: 780, last_round: "NASDAQ: TXG",
    strategic_move: "Xenium Prime 5K launched; spatial multiomics expansion; NASDAQ: TXG valuation declined 93% from peak",
    strategic_tag: "Product",
    core_offering: "Single-cell and spatial genomics technology company providing instruments, software, and reagents for biological and clinical research.",
    key_features: ["Chromium single-cell sequencing platform", "Xenium spatial transcriptomics instrument", "Visium spatial genomics array", "Cell Ranger & Loupe analysis software", "Spatial multiomics pipeline"],
    revenue_model: "Instrument capital sales / Recurring consumable reagent kits / Software licensing",
    operational_structure: "HQ Pleasanton CA; NASDAQ: TXG; global lab distribution network; ~1,380 employees",
    distribution_channels: ["Direct sales to biomedical research labs", "Global distributor network", "Academic consortium partnerships"],
    ai_capabilities: "ML for cell type classification; spatial deconvolution algorithms; automated quality control; AI-assisted data interpretation tools",
  },
  "guardant-health": {
    name: "Guardant Health",
    country: "US", flag: "🇺🇸",
    sector: "Liquid Biopsy / Oncology", sector_category: "Biotech / Health",
    website: "guardanthealth.com",
    valuation_b: 3.5,
    val_trend: [9, 5, 4, 3.5],
    rev_trend: [413, 546, 720],
    hc_trend: [1700, 1900, 2000], talent_signal: "Stable →",
    traffic_trend: [3, 3.5, 4],
    linkedin_followers: "95K",
    glassdoor_rating: 3.9,
    sentiment: "POSITIVE",
    total_raised_m: 1600, last_round: "NASDAQ: GH",
    strategic_move: "FDA approved Shield colorectal cancer screening test; NASDAQ: GH; revenue +32% YoY 2024",
    strategic_tag: "FDA",
    core_offering: "Liquid biopsy company offering blood-based cancer detection and monitoring via circulating tumor DNA analysis.",
    key_features: ["Shield — FDA-approved colorectal cancer screening", "Guardant360 CDx companion diagnostic", "Guardant Reveal — MRD monitoring", "LUNAR program for multi-cancer early detection", "Genomic insights dashboard for oncologists"],
    revenue_model: "Per-test diagnostic fees / Insurance reimbursement (Shield + Guardant360)",
    operational_structure: "HQ Redwood City CA; NASDAQ: GH; ~2,000 employees",
    distribution_channels: ["Physician/oncologist orders", "Hospital lab partnerships", "Direct-to-patient (Shield screening)"],
    ai_capabilities: "ML for cfDNA variant calling; cancer origin classification; minimal residual disease detection; longitudinal monitoring algorithms",
  },
  "ai-medical-service": {
    name: "AI Medical Service",
    country: "JP", flag: "🇯🇵",
    sector: "Medical AI / Endoscopy", sector_category: "Biotech / Health",
    website: "ai-ms.com",
    valuation_b: 0.3,
    val_trend: [0.15, 0.25, 0.3],
    rev_trend: [8, 15, 25],
    hc_trend: [170, 200, 230], talent_signal: "Stable →",
    traffic_trend: [0.3, 0.4, 0.5],
    linkedin_followers: "12K",
    glassdoor_rating: 4.0,
    sentiment: "POSITIVE",
    total_raised_m: 45, last_round: "Series B",
    strategic_move: "PMDA clearance in Japan; expanding endoscopy AI to SE Asia; international partnership pipeline",
    strategic_tag: "Regulatory",
    core_offering: "Japanese medical AI company providing real-time endoscopy AI systems that detect polyps and gastrointestinal lesions during colonoscopy procedures.",
    key_features: ["GI Genius real-time polyp detection AI", "PMDA (Japan) & CE Mark regulatory clearance", "Integration with Olympus/Fujifilm endoscopes", "Multi-class pathology classification", "SE Asia expansion pipeline"],
    revenue_model: "Per-procedure licensing fee / Hospital system licensing / Equipment bundling",
    operational_structure: "HQ Tokyo Japan; SE Asia expansion; ~230 employees",
    distribution_channels: ["Hospital system partnerships", "Endoscope manufacturer integrations (Olympus)", "Regional medical distributor agreements"],
    ai_capabilities: "Deep learning computer vision for endoscopic image analysis; real-time lesion detection; polyp size estimation; multi-class pathology classification",
  },
  "arm": {
    name: "Arm Holdings",
    country: "UK", flag: "🇬🇧",
    sector: "Semiconductor IP", sector_category: "Deep Tech",
    website: "arm.com",
    valuation_b: 130,
    val_trend: [54, 80, 130],
    rev_trend: [2703, 2570, 3230],
    hc_trend: [6000, 5800, 6000], talent_signal: "Stable →",
    traffic_trend: [14, 16, 18],
    linkedin_followers: "2.1M",
    glassdoor_rating: 3.8,
    sentiment: "POSITIVE",
    total_raised_m: 1200, last_round: "NASDAQ: ARM",
    strategic_move: "Data center CPU traction; Total Design expanded; NASDAQ: ARM +60% YoY; AI chip licensing surge",
    strategic_tag: "Public",
    core_offering: "Semiconductor IP licensor providing CPU and GPU architectures powering virtually all smartphones and a growing share of data center and AI chips.",
    key_features: ["Cortex-A/M/R CPU IP families", "Mali GPU IP", "Neoverse data center CPUs (AWS Graviton)", "Ethos NPU for on-device AI", "Total Design ecosystem & CSS solutions"],
    revenue_model: "IP license fees (upfront) + Royalties per chip shipped",
    operational_structure: "HQ Cambridge UK; NASDAQ: ARM; design centers globally; ~6,000 employees",
    distribution_channels: ["IP licensing to chip designers (Apple, Qualcomm, Samsung, NVIDIA)", "Royalty collection on shipped silicon"],
    ai_capabilities: "Ethos NPU IP for on-device AI inference; Arm AI accelerator architectures; ML software optimization (ArmNN); AI chip licensing for data centers",
  },
  "brain-corporation": {
    name: "Brain Corp",
    country: "US", flag: "🇺🇸",
    sector: "Robotics Software", sector_category: "AI / Tech",
    website: "braincorp.com",
    valuation_b: 0.75,
    val_trend: [0.6, 0.75, 0.75],
    rev_trend: [22, 30, 42],
    hc_trend: [190, 200, 210], talent_signal: "Stable →",
    traffic_trend: [0.7, 0.85, 1],
    linkedin_followers: "18K",
    glassdoor_rating: 3.8,
    sentiment: "NEUTRAL",
    total_raised_m: 135, last_round: "Series C",
    strategic_move: "25,000+ robots globally; BrainOS 5.0 launched; Walmart and Kroger key customers",
    strategic_tag: "Product",
    core_offering: "Robotics software company providing the BrainOS autonomous operating system for commercial floor-cleaning and logistics robots.",
    key_features: ["BrainOS autonomous navigation platform", "25,000+ deployed robots globally", "Fleet management & analytics software", "Integration with Tennant, Nilfisk, SoftBank robots", "Walmart & Kroger retail deployments"],
    revenue_model: "SaaS subscription per robot (monthly) / Technology licensing to OEM manufacturers",
    operational_structure: "HQ San Diego CA; ~210 employees; OEM-driven distribution model",
    distribution_channels: ["OEM robot manufacturer partnerships", "Enterprise direct sales (retail, airports, logistics)"],
    ai_capabilities: "Computer vision for autonomous navigation; SLAM (simultaneous localization and mapping); obstacle detection; fleet route optimization ML",
  },
  "cambridge-mobile-telematics": {
    name: "Cambridge Mobile Telematics",
    country: "US", flag: "🇺🇸",
    sector: "Telematics / InsurTech", sector_category: "Fintech",
    website: "cmtelematics.com",
    valuation_b: 1.2,
    val_trend: [0.9, 1.1, 1.2],
    rev_trend: [75, 95, 130],
    hc_trend: [480, 500, 550], talent_signal: "Stable →",
    traffic_trend: [1, 1.2, 1.5],
    linkedin_followers: "22K",
    glassdoor_rating: 3.9,
    sentiment: "POSITIVE",
    total_raised_m: 400, last_round: "Series C",
    strategic_move: "AI crash detection launched; 20 carrier partners; DriveWell platform expansion; Series C",
    strategic_tag: "Series C",
    core_offering: "Mobile telematics platform using smartphone sensors to measure driving behavior and provide UBI (usage-based insurance) programs for 20+ carriers.",
    key_features: ["DriveWell smartphone telematics platform", "AI-powered crash detection & reconstruction", "20+ insurance carrier integrations", "No additional hardware required", "Fleet safety scoring & coaching"],
    revenue_model: "SaaS per-driver subscription fee / Per-program fee to insurance carriers",
    operational_structure: "HQ Cambridge MA (MIT spinout); ~550 employees",
    distribution_channels: ["B2B to insurance carriers (UBI programs)", "Fleet management company partnerships", "White-label OEM auto integration"],
    ai_capabilities: "ML crash detection and reconstruction; driving behavior scoring algorithms; risk prediction models; real-time trip analysis; telematics AI",
  },
  "see-jane-farm": {
    name: "Plenty",
    country: "US", flag: "🇺🇸",
    sector: "Vertical Farming", sector_category: "Consumer",
    website: "plenty.ag",
    valuation_b: 1.1,
    val_trend: [1.8, 1.5, 1.3, 1.1],
    rev_trend: [18, 30, 42],
    hc_trend: [550, 500, 480], talent_signal: "Layoffs ↓",
    traffic_trend: [0.8, 1, 1.2],
    linkedin_followers: "28K",
    glassdoor_rating: 3.4,
    sentiment: "CAUTIONARY",
    total_raised_m: 940, last_round: "Series E",
    strategic_move: "Walmart partnership expanded; Virginia farm operational; CapEx-intensive; profitability timeline uncertain",
    strategic_tag: "Expansion",
    core_offering: "Indoor vertical farming company using AI-controlled growing environments to produce pesticide-free leafy greens year-round at commercial scale.",
    key_features: ["AI-controlled indoor grow environments", "Walmart strategic partnership (primary retailer)", "Compton CA + Richmond VA mega-farms", "Pesticide-free produce", "Year-round consistent yield regardless of season"],
    revenue_model: "Direct product sales to retailers (Walmart primary) / Wholesale grocery distribution",
    operational_structure: "HQ San Francisco CA; farms in Compton CA & Richmond VA; ~480 employees",
    distribution_channels: ["Retail partnership (Walmart primary)", "Wholesale grocery distribution"],
    ai_capabilities: "AI climate control for plant optimization; computer vision for plant health monitoring; ML yield prediction; automated harvest scheduling",
  },
  "wework": {
    name: "WeWork",
    country: "US", flag: "🇺🇸",
    sector: "Flexible Workspaces", sector_category: "Consumer",
    website: "wework.com",
    valuation_b: 0.1,
    val_trend: [47, 10, 0.5, 0.05, 0.1],
    rev_trend: [2567, 3600, 4100],
    hc_trend: [12000, 2800, 2600], talent_signal: "Layoffs ↓",
    traffic_trend: [40, 30, 25],
    linkedin_followers: "1.2M",
    glassdoor_rating: 2.8,
    sentiment: "NEGATIVE",
    total_raised_m: 12000, last_round: "Post-Ch.11",
    strategic_move: "Emerged from Chapter 11 bankruptcy Nov 2023; shed 50% of leases; balance sheet fragile",
    strategic_tag: "Restructure",
    core_offering: "Flexible workspace network providing shared offices, private suites, and enterprise workspace-as-a-service across 600+ global locations post-restructuring.",
    key_features: ["Flexible desk & private office memberships", "All Access pass (500+ locations)", "Enterprise WeWork for large companies", "Post-Chapter 11 restructured lease portfolio", "On-demand meeting rooms & event spaces"],
    revenue_model: "Monthly membership fees / Enterprise workspace contracts / Revenue share with landlords",
    operational_structure: "HQ New York NY; 600+ locations globally (reduced from 750+); ~2,600 employees post-restructuring",
    distribution_channels: ["Direct consumer/SMB memberships online", "Enterprise B2B sales", "Commercial real estate broker partnerships"],
    ai_capabilities: "Occupancy optimization algorithms; demand-based pricing; space utilization analytics; tenant match recommendation engine",
  },
  "clutter": {
    name: "Clutter",
    country: "US", flag: "🇺🇸",
    sector: "On-Demand Storage", sector_category: "Consumer",
    website: "clutter.com",
    valuation_b: 0.2,
    val_trend: [0.9, 0.5, 0.25, 0.2],
    rev_trend: [45, 60, 70],
    hc_trend: [1200, 800, 700], talent_signal: "Layoffs ↓",
    traffic_trend: [1.2, 1, 0.8],
    linkedin_followers: "15K",
    glassdoor_rating: 3.2,
    sentiment: "NEGATIVE",
    total_raised_m: 300, last_round: "Series E",
    strategic_move: "Exited several US metro markets; restructured; unit economics challenged; competition from PODS",
    strategic_tag: "Restructure",
    core_offering: "On-demand valet storage service that picks up, stores, and delivers urban consumer belongings via a tech-enabled logistics platform with photo inventory.",
    key_features: ["Door-to-door pickup & delivery service", "Photo catalog of all stored items (app)", "Climate-controlled storage facilities", "Moving & storage bundled plans", "On-demand item retrieval"],
    revenue_model: "Monthly storage subscription / Moving service fees / Add-on services",
    operational_structure: "HQ Los Angeles CA; operations in 10+ US metro areas (reduced); ~700 employees",
    distribution_channels: ["Direct consumer web/app", "Moving company partnership referrals", "Storage marketplace listings"],
    ai_capabilities: "Computer vision for automated item cataloging; ML demand & inventory forecasting; route optimization for pickup/delivery; dynamic pricing algorithms",
  },
  "accelbyte-inc": {
    name: "AccelByte",
    country: "US", flag: "🇺🇸",
    sector: "Gaming Backend SaaS", sector_category: "SaaS / B2B",
    website: "accelbyte.io",
    valuation_b: 0.25,
    val_trend: [0.15, 0.2, 0.25],
    rev_trend: [7, 12, 20],
    hc_trend: [150, 200, 250], talent_signal: "Hiring ↑",
    traffic_trend: [0.3, 0.5, 0.6],
    linkedin_followers: "18K",
    glassdoor_rating: 4.2,
    sentiment: "POSITIVE",
    total_raised_m: 40, last_round: "Series B",
    strategic_move: "3 AAA studio deals signed; AI matchmaking launched; Series B; gaming backend niche leader",
    strategic_tag: "Deals",
    core_offering: "Gaming backend-as-a-service providing multiplayer infrastructure, player identity, and analytics APIs for AAA game studios and publishers.",
    key_features: ["Lobby, matchmaking & session management APIs", "Player identity & authentication system", "Game telemetry & analytics platform", "AI-powered skill-based matchmaking", "Cloud-agnostic BaaS deployment"],
    revenue_model: "SaaS subscription (per studio / per active player tier)",
    operational_structure: "HQ Seattle WA + Yogyakarta Indonesia; ~250 employees; dual-HQ model",
    distribution_channels: ["Direct enterprise sales to AAA game studios", "Gaming middleware & engine partnerships"],
    ai_capabilities: "AI skill-based matchmaking for balanced game sessions; player behavior ML; churn prediction; anti-cheat fraud detection; latency optimization",
  },
  "airtime": {
    name: "Airtime",
    country: "US", flag: "🇺🇸",
    sector: "Social AI / Entertainment", sector_category: "Consumer",
    website: "airtime.com",
    valuation_b: 0.08,
    val_trend: [0.4, 0.15, 0.1, 0.08],
    rev_trend: [3, 5, 8],
    hc_trend: [100, 80, 70], talent_signal: "Layoffs ↓",
    traffic_trend: [0.8, 0.6, 0.5],
    linkedin_followers: "8K",
    glassdoor_rating: 3.1,
    sentiment: "CAUTIONARY",
    total_raised_m: 25, last_round: "Seed",
    strategic_move: "Pivoted to AI companions; team reduced; monetization still unproven; crowded social AI space",
    strategic_tag: "Pivot",
    core_offering: "Social entertainment platform combining group watch parties, real-time reactions, and AI companion features targeting Gen Z social experiences.",
    key_features: ["Group watch parties with reactions", "AI companion personality features", "Music listening rooms", "Creator broadcast tools", "Real-time co-viewing chat"],
    revenue_model: "Freemium / In-app purchases / Virtual currency / Subscriptions (nascent)",
    operational_structure: "HQ Los Angeles CA; ~70 employees (reduced from 100); startup mode",
    distribution_channels: ["App stores (iOS/Android)", "Social media user acquisition"],
    ai_capabilities: "AI companion personality engine; recommendation engine for content; NLP for content moderation; social graph analysis",
  },

  // ─── 14 Additional Portfolio Companies ────────────────────────────────────────
  "canva": {
    name: "Canva",
    country: "AU", flag: "🇦🇺",
    sector: "Design / SaaS", sector_category: "SaaS / B2B",
    website: "canva.com",
    valuation_b: 26,
    val_trend: [6, 15, 40, 26],
    rev_trend: [700, 1400, 2500],
    hc_trend: [2500, 4000, 5000], talent_signal: "Stable →",
    traffic_trend: [300, 420, 580],
    linkedin_followers: "1.2M",
    glassdoor_rating: 4.5,
    sentiment: "POSITIVE",
    total_raised_m: 1500, last_round: "Pre-IPO",
    strategic_move: "Launched Magic Studio AI suite; acquired Leonardo AI; revenue run rate $2.5B+",
    strategic_tag: "IPO Prep",
    core_offering: "Visual communication platform empowering 200M+ users to design presentations, social media graphics, documents, and marketing materials with AI-powered tools.",
    key_features: ["Magic Studio AI design suite", "200M+ registered users globally", "Leonardo AI acquisition (generative images)", "Canva for Teams enterprise tier", "60+ languages, 190+ countries"],
    revenue_model: "Freemium / Canva Pro ($15/mo) / Canva for Teams / Enterprise contracts",
    operational_structure: "HQ Sydney Australia; US offices in Austin TX; ~5,000 employees globally",
    distribution_channels: ["Direct consumer web/app (freemium)", "B2B enterprise sales", "Education partnerships"],
    ai_capabilities: "Magic Studio generative AI (text-to-image, background removal, Magic Write); AI design suggestions; automatic resizing; AI video generation",
  },
  "stripe": {
    name: "Stripe",
    country: "US", flag: "🇺🇸",
    sector: "Payments / Fintech", sector_category: "Fintech",
    website: "stripe.com",
    valuation_b: 65,
    val_trend: [36, 95, 50, 65],
    rev_trend: [7400, 10000, 14400],
    hc_trend: [7000, 8000, 9000], talent_signal: "Stable →",
    traffic_trend: [220, 270, 310],
    linkedin_followers: "1.1M",
    glassdoor_rating: 4.3,
    sentiment: "POSITIVE",
    total_raised_m: 9200, last_round: "Series I",
    strategic_move: "Revenue hit $14.4B (2024); launched Stablecoin Payments; IPO preparation underway",
    strategic_tag: "IPO Prep",
    core_offering: "Global payments infrastructure providing APIs for internet businesses to accept payments, manage subscriptions, and handle financial operations at scale.",
    key_features: ["Stripe Payments — global card & bank acceptance", "Stripe Connect for marketplace payouts", "Stripe Billing for subscriptions", "Stripe Capital for business lending", "Stablecoin-based global payments"],
    revenue_model: "Transaction fee (2.9% + 30¢) / Flat-rate enterprise pricing / SaaS billing add-ons",
    operational_structure: "HQ San Francisco CA & Dublin IE; global offices; ~9,000 employees",
    distribution_channels: ["API-first developer self-serve", "Enterprise direct sales", "Stripe Partner ecosystem"],
    ai_capabilities: "ML fraud detection (Stripe Radar); AI-assisted chargeback management; revenue recognition automation; smart retry logic; adaptive acceptance optimization",
  },
  "anthropic": {
    name: "Anthropic",
    country: "US", flag: "🇺🇸",
    sector: "AI / Foundation Models", sector_category: "AI / Tech",
    website: "anthropic.com",
    valuation_b: 61,
    val_trend: [1.5, 4, 18, 61],
    rev_trend: [300, 1300, 4400],
    hc_trend: [200, 500, 1800], talent_signal: "Hiring ↑",
    traffic_trend: [10, 35, 85],
    linkedin_followers: "460K",
    glassdoor_rating: 4.6,
    sentiment: "POSITIVE",
    total_raised_m: 12000, last_round: "Series E",
    strategic_move: "Raised $7.5B at $61B; Claude 3.5 Sonnet led enterprise; Amazon & Google partnerships",
    strategic_tag: "Fundraise",
    core_offering: "AI safety company developing Claude — a family of frontier language models focused on helpfulness, harmlessness, and honesty for enterprise and consumer use.",
    key_features: ["Claude 3.5 Sonnet — leading enterprise AI model", "Claude API for developers", "Claude.ai consumer chat product", "Constitutional AI safety methodology", "Amazon & Google cloud distribution"],
    revenue_model: "API usage fees (per-token) / Claude Pro subscription / Enterprise contracts (AWS/GCP)",
    operational_structure: "HQ San Francisco CA; ~1,800 employees; safety-focused research culture",
    distribution_channels: ["API platform (direct & cloud marketplace)", "Claude.ai consumer web/mobile", "Enterprise direct sales"],
    ai_capabilities: "Constitutional AI for safety alignment; frontier LLM (Claude 3.5); multi-modal vision; long context window (200K tokens); computer use agent capabilities",
  },
  "scale-ai": {
    name: "Scale AI",
    country: "US", flag: "🇺🇸",
    sector: "AI Data Platform", sector_category: "AI / Tech",
    website: "scale.com",
    valuation_b: 14,
    val_trend: [3.5, 7.3, 14],
    rev_trend: [250, 670, 1400],
    hc_trend: [500, 700, 1000], talent_signal: "Hiring ↑",
    traffic_trend: [8, 14, 18],
    linkedin_followers: "150K",
    glassdoor_rating: 4.0,
    sentiment: "POSITIVE",
    total_raised_m: 1500, last_round: "Series F",
    strategic_move: "DoD & US government contracts; SEAL-T defense data platform; enterprise AI data market leader",
    strategic_tag: "Government",
    core_offering: "AI data infrastructure company providing labeled training data, RLHF services, and evaluation platforms for frontier model development and enterprise AI.",
    key_features: ["Human-in-the-loop data labeling at scale", "RLHF pipeline for LLM fine-tuning", "Scale Donovan defense intelligence platform", "Enterprise evaluation benchmarks", "Government & defense AI contracts"],
    revenue_model: "Per-task labeling fees / SaaS platform subscription / Government contract revenue",
    operational_structure: "HQ San Francisco CA; ~1,000 employees; defense unit (Scale Federal)",
    distribution_channels: ["Direct enterprise sales", "US government procurement", "AI lab partnerships (OpenAI, Meta, Microsoft)"],
    ai_capabilities: "Active learning for data efficiency; quality ML for annotation accuracy; RLHF infrastructure; automated data pipeline tooling; evaluation LLM-as-judge",
  },
  "databricks": {
    name: "Databricks",
    country: "US", flag: "🇺🇸",
    sector: "Data / AI Platform", sector_category: "SaaS / B2B",
    website: "databricks.com",
    valuation_b: 62,
    val_trend: [6.2, 28, 43, 62],
    rev_trend: [1000, 1600, 2400],
    hc_trend: [5000, 6000, 7500], talent_signal: "Hiring ↑",
    traffic_trend: [20, 25, 30],
    linkedin_followers: "750K",
    glassdoor_rating: 4.3,
    sentiment: "POSITIVE",
    total_raised_m: 4300, last_round: "Series J",
    strategic_move: "Raised $15.3B at $62B; acquired Tabular & Mosaic ML; $2.4B+ revenue run rate",
    strategic_tag: "Fundraise",
    core_offering: "Unified data intelligence platform combining data engineering, data warehousing, and AI/ML on the open Delta Lake standard for enterprise analytics.",
    key_features: ["Delta Lake open-source data format", "Databricks SQL (cloud data warehouse)", "ML Flow MLOps platform", "Unity Catalog data governance", "DBRX — proprietary open LLM"],
    revenue_model: "Compute consumption pricing (DBU) / Annual platform subscription / Professional services",
    operational_structure: "HQ San Francisco CA; global offices; ~7,500 employees; multi-cloud (AWS/Azure/GCP)",
    distribution_channels: ["Cloud marketplace (all 3 major clouds)", "Direct enterprise sales", "Open-source community (Apache Spark lineage)"],
    ai_capabilities: "DBRX open-source LLM; AutoML for feature engineering; LLMOps via MLFlow; vector search for RAG; AI/BI natural language analytics",
  },
  "rippling": {
    name: "Rippling",
    country: "US", flag: "🇺🇸",
    sector: "HR / Finance SaaS", sector_category: "SaaS / B2B",
    website: "rippling.com",
    valuation_b: 16.8,
    val_trend: [6.5, 11.5, 16.8],
    rev_trend: [500, 900, 1400],
    hc_trend: [1800, 2500, 3500], talent_signal: "Hiring ↑",
    traffic_trend: [10, 15, 20],
    linkedin_followers: "350K",
    glassdoor_rating: 4.4,
    sentiment: "POSITIVE",
    total_raised_m: 2000, last_round: "Series G",
    strategic_move: "Series G at $16.8B; launched Rippling Spend; expanding into UK, India, Canada",
    strategic_tag: "Series G",
    core_offering: "Workforce management platform unifying HR, IT, and finance in a single system of record to manage employees, payroll, benefits, devices, and spend globally.",
    key_features: ["Global payroll (185+ countries)", "Rippling HCM — HR automation", "IT device management & provisioning", "Rippling Spend — corporate card & expenses", "Unity API — custom workflow builder"],
    revenue_model: "Per-employee SaaS subscription (modular per-product pricing)",
    operational_structure: "HQ San Francisco CA; UK & India offices; ~3,500 employees",
    distribution_channels: ["Direct SMB/mid-market sales", "Self-serve online onboarding", "HR consultant & broker channel"],
    ai_capabilities: "AI-powered compliance monitoring; automated onboarding workflows; predictive attrition scoring; intelligent benefits recommendation; payroll anomaly detection",
  },
  "figma": {
    name: "Figma",
    country: "US", flag: "🇺🇸",
    sector: "Design SaaS", sector_category: "SaaS / B2B",
    website: "figma.com",
    valuation_b: 12.5,
    val_trend: [10, 20, 12.5],
    rev_trend: [400, 600, 750],
    hc_trend: [800, 1000, 1400], talent_signal: "Hiring ↑",
    traffic_trend: [20, 28, 35],
    linkedin_followers: "400K",
    glassdoor_rating: 4.7,
    sentiment: "POSITIVE",
    total_raised_m: 750, last_round: "Pre-IPO",
    strategic_move: "Adobe $20B acquisition blocked by EU/DoJ; IPO filing 2025; Figma AI & Figma Sites launched",
    strategic_tag: "IPO Prep",
    core_offering: "Collaborative design platform where product teams create, prototype, and ship digital products — the de facto standard for UI/UX design and product workflows.",
    key_features: ["Figma Design — real-time collaborative UI editor", "Figma Prototyping & Dev Mode", "Figma AI (Make Designs, auto-layout)", "Figma Sites — web publishing", "FigJam online whiteboard"],
    revenue_model: "Freemium / Figma Professional ($15/editor/mo) / Figma Organization ($45/editor/mo) / Enterprise",
    operational_structure: "HQ San Francisco CA; New York & London offices; ~1,400 employees",
    distribution_channels: ["Freemium self-serve web (viral adoption)", "Enterprise direct sales", "Education program (free for students)"],
    ai_capabilities: "AI layout suggestions (Auto Layout AI); generative UI from prompts (Make Designs); asset search ML; component variant prediction; code generation from design",
  },
  "cohere": {
    name: "Cohere",
    country: "CA", flag: "🇨🇦",
    sector: "Enterprise AI", sector_category: "AI / Tech",
    website: "cohere.com",
    valuation_b: 5.5,
    val_trend: [2.1, 5.5],
    rev_trend: [50, 150, 400],
    hc_trend: [200, 350, 700], talent_signal: "Hiring ↑",
    traffic_trend: [2, 4, 7],
    linkedin_followers: "120K",
    glassdoor_rating: 4.4,
    sentiment: "POSITIVE",
    total_raised_m: 1000, last_round: "Series D",
    strategic_move: "Oracle & Google Cloud partnerships; Command R+ leads enterprise RAG; $1B revenue run rate target",
    strategic_tag: "Partnership",
    core_offering: "Enterprise-focused AI company providing large language models and NLP tools for search, summarization, and RAG applications — deployed on private cloud.",
    key_features: ["Command R+ — frontier model for enterprise RAG", "Embed — semantic search embeddings", "Rerank — search relevance reranking", "On-prem & private cloud deployment", "Coral enterprise AI assistant"],
    revenue_model: "API usage fees / Enterprise SaaS contracts / Cloud marketplace (AWS/Azure/GCP/OCI)",
    operational_structure: "HQ Toronto Canada; NYC & London offices; ~700 employees",
    distribution_channels: ["API platform (direct)", "Cloud marketplace listings", "Enterprise direct sales"],
    ai_capabilities: "Enterprise RAG pipeline; multilingual LLM (100+ languages); private cloud deployment; fine-tuning for domain-specific use; semantic search & reranking",
  },
  "anduril-industries": {
    name: "Anduril Industries",
    country: "US", flag: "🇺🇸",
    sector: "Defense / AI Tech", sector_category: "Deep Tech",
    website: "anduril.com",
    valuation_b: 28,
    val_trend: [4.6, 8.5, 14, 28],
    rev_trend: [200, 500, 1200],
    hc_trend: [1500, 2500, 3500], talent_signal: "Hiring ↑",
    traffic_trend: [3, 5, 8],
    linkedin_followers: "180K",
    glassdoor_rating: 4.3,
    sentiment: "POSITIVE",
    total_raised_m: 4000, last_round: "Series F",
    strategic_move: "Raised $1.5B at $28B; TITAN autonomous ground vehicle DoD contract; AUKUS submarine AI",
    strategic_tag: "Fundraise",
    core_offering: "Defense technology company building autonomous weapon systems and AI-driven command software for the US military and allied nations.",
    key_features: ["Lattice — AI command & control software", "Ghost autonomous aerial systems", "TITAN autonomous ground vehicle", "Menace counter-drone system", "US DoD & AUKUS contracts"],
    revenue_model: "Government defense contracts / Technology licensing to allied militaries",
    operational_structure: "HQ Costa Mesa CA; offices in Austin TX & Washington DC; ~3,500 employees",
    distribution_channels: ["US government procurement (DoD)", "Five Eyes allied nation contracts", "Defense prime partnerships"],
    ai_capabilities: "AI sensor fusion for battlespace awareness; autonomous mission planning; computer vision for target detection; swarm drone coordination; real-time threat assessment",
  },
  "brex": {
    name: "Brex",
    country: "US", flag: "🇺🇸",
    sector: "Corporate Fintech", sector_category: "Fintech",
    website: "brex.com",
    valuation_b: 12,
    val_trend: [7.4, 12.3, 12],
    rev_trend: [150, 350, 500],
    hc_trend: [1200, 950, 850], talent_signal: "Stable →",
    traffic_trend: [5, 6, 7],
    linkedin_followers: "200K",
    glassdoor_rating: 3.9,
    sentiment: "NEUTRAL",
    total_raised_m: 1500, last_round: "Series D",
    strategic_move: "Pivoted to enterprise; shed SMB customers; Brex AI expense agent; $500M revenue target",
    strategic_tag: "Pivot",
    core_offering: "AI-powered spend management platform providing corporate cards, expense management, and travel for global companies replacing legacy expense tools.",
    key_features: ["Brex Corporate Card (no personal guarantee)", "AI-powered expense automation", "Travel booking & management", "Global accounts & multi-currency", "Brex Budget software for spend control"],
    revenue_model: "Interchange fees on card spend / SaaS platform fee / Travel booking commissions",
    operational_structure: "HQ San Francisco CA; global finance operations; ~850 employees (post-SMB pivot)",
    distribution_channels: ["Direct enterprise sales", "Self-serve online onboarding", "VC-backed startup ecosystem"],
    ai_capabilities: "AI receipt matching & auto-categorization; expense anomaly detection; AI travel policy compliance; smart budget forecasting; natural language expense queries",
  },
  "notion": {
    name: "Notion",
    country: "US", flag: "🇺🇸",
    sector: "Productivity SaaS", sector_category: "SaaS / B2B",
    website: "notion.so",
    valuation_b: 10,
    val_trend: [2, 10, 10],
    rev_trend: [200, 400, 600],
    hc_trend: [500, 700, 900], talent_signal: "Stable →",
    traffic_trend: [50, 80, 100],
    linkedin_followers: "550K",
    glassdoor_rating: 4.4,
    sentiment: "POSITIVE",
    total_raised_m: 343, last_round: "Series C",
    strategic_move: "Notion AI hits 3M+ paying users; AI workspace leader; Notion Calendar; IPO exploration",
    strategic_tag: "Product",
    core_offering: "All-in-one connected workspace combining notes, databases, wikis, and project management — with deeply integrated AI writing and automation features.",
    key_features: ["AI writing assistant (3M+ paying users)", "Connected docs, wikis & databases", "Notion Projects (Jira alternative)", "Notion Calendar (Google Calendar integration)", "API & 100+ integrations"],
    revenue_model: "Freemium / Plus ($10/mo) / Business ($18/mo) / Enterprise custom pricing",
    operational_structure: "HQ San Francisco CA; NYC & Tokyo offices; ~900 employees",
    distribution_channels: ["Freemium self-serve web/app (PLG)", "Direct enterprise sales", "Education segment (free plan)"],
    ai_capabilities: "Notion AI writing assistant; auto-summarize meeting notes; Q&A over workspace docs; smart task extraction; AI database autofill; intelligent search",
  },
  "harvey-ai": {
    name: "Harvey",
    country: "US", flag: "🇺🇸",
    sector: "Legal AI", sector_category: "AI / Tech",
    website: "harvey.ai",
    valuation_b: 3,
    val_trend: [0.5, 1.5, 3],
    rev_trend: [10, 50, 200],
    hc_trend: [100, 200, 500], talent_signal: "Hiring ↑",
    traffic_trend: [0.5, 1.5, 3],
    linkedin_followers: "45K",
    glassdoor_rating: 4.5,
    sentiment: "POSITIVE",
    total_raised_m: 400, last_round: "Series C",
    strategic_move: "Series C $300M at $3B; A&O Shearman partnership; 200+ law firms using Harvey globally",
    strategic_tag: "Series C",
    core_offering: "AI platform for legal professionals — automating contract analysis, due diligence, legal research, and document drafting for law firms and in-house counsel.",
    key_features: ["Legal document analysis & summarization", "Contract review & redlining AI", "Due diligence automation", "Legal research across case law & statutes", "Hallucination-resistant legal citations"],
    revenue_model: "SaaS subscription (per-seat, enterprise annual contracts)",
    operational_structure: "HQ San Francisco CA; New York & London offices; ~500 employees",
    distribution_channels: ["Direct enterprise sales to law firms", "In-house legal team sales", "Big 4 advisory partnerships"],
    ai_capabilities: "Fine-tuned LLMs on legal corpora; hallucination detection for legal accuracy; document comparison & diff AI; NLP for contract clause extraction; case law retrieval",
  },
  "mistral-ai": {
    name: "Mistral AI",
    country: "FR", flag: "🇫🇷",
    sector: "AI / Foundation Models", sector_category: "AI / Tech",
    website: "mistral.ai",
    valuation_b: 6,
    val_trend: [0.2, 2, 6],
    rev_trend: [20, 100, 400],
    hc_trend: [30, 100, 250], talent_signal: "Hiring ↑",
    traffic_trend: [3, 8, 15],
    linkedin_followers: "160K",
    glassdoor_rating: 4.6,
    sentiment: "POSITIVE",
    total_raised_m: 1100, last_round: "Series B",
    strategic_move: "Raised $1.08B at $6.2B; Mistral Large 2 & Le Chat launched; Europe's leading AI lab",
    strategic_tag: "Fundraise",
    core_offering: "European frontier AI company developing open and proprietary language models — Mistral Large, Codestral, and Le Chat — for enterprise and developer use.",
    key_features: ["Mistral Large 2 — frontier closed model", "Mixtral 8x7B — leading open-source MoE model", "Le Chat consumer AI assistant", "Codestral coding model", "La Plateforme API for developers"],
    revenue_model: "API usage fees / Enterprise licensing / La Plateforme subscription",
    operational_structure: "HQ Paris France; ~250 employees; European AI champion with global developer reach",
    distribution_channels: ["La Plateforme API direct", "Cloud marketplace (Azure, Google, AWS)", "Open-source community (Hugging Face)"],
    ai_capabilities: "Mixture-of-Experts (MoE) architecture; multilingual frontier models; code generation (Codestral); function calling & tool use; RAG-optimized embeddings",
  },
  "waymo": {
    name: "Waymo",
    country: "US", flag: "🇺🇸",
    sector: "Autonomous Vehicles", sector_category: "AI / Tech",
    website: "waymo.com",
    valuation_b: 45,
    val_trend: [30, 35, 45],
    rev_trend: [100, 300, 800],
    hc_trend: [2000, 2500, 3000], talent_signal: "Hiring ↑",
    traffic_trend: [10, 15, 20],
    linkedin_followers: "350K",
    glassdoor_rating: 4.2,
    sentiment: "POSITIVE",
    total_raised_m: 11200, last_round: "Series C++",
    strategic_move: "5M+ paid robotaxi rides (2024); SF, LA, Phoenix operations; Uber partnership announced",
    strategic_tag: "Commercial",
    core_offering: "Autonomous ride-hailing company operating the Waymo One robotaxi service — the world's first commercial fully driverless taxi network at scale.",
    key_features: ["Waymo One robotaxi service (SF, LA, Phoenix)", "5M+ paid driverless rides (2024)", "Waymo Driver Level 4 autonomy stack", "Uber partnership for ride distribution", "5th-gen Waymo sensor suite"],
    revenue_model: "Per-ride robotaxi fare / Technology licensing / Trucking (Waymo Via)",
    operational_structure: "HQ Mountain View CA (Alphabet subsidiary); ~3,000 employees; fleet of 700+ vehicles",
    distribution_channels: ["Waymo One app (direct consumer)", "Uber marketplace integration", "B2B logistics (Waymo Via trucking)"],
    ai_capabilities: "Level 4 autonomous driving AI; multimodal sensor fusion (lidar/radar/camera); real-time 3D scene understanding; long-tail edge case training; fleet remote monitoring",
  },
  "ainnovation": {
    name: "AInnovation",
    country: "CN", flag: "🇨🇳",
    sector: "Enterprise AI", sector_category: "AI / Tech",
    website: "ainnovation.com",
    valuation_b: 0.38,
    val_trend: [1.2, 0.8, 0.4, 0.38],
    rev_trend: [240, 330, 410, 480],
    hc_trend: [900, 962, 962, 1000], talent_signal: "Hiring ↑",
    traffic_trend: null,
    linkedin_followers: "1.5K",
    glassdoor_rating: null,
    sentiment: "NEUTRAL",
    total_raised_m: null, last_round: "Series C",
    strategic_move: "Enterprise AI solutions for industrial and retail sectors across China",
    strategic_tag: "Growth",
    core_offering: "Enterprise AI platform delivering computer vision, machine learning and intelligent automation solutions for industrial manufacturing and retail operations in China.",
    key_features: ["Computer vision for quality inspection", "AI-powered retail analytics", "Industrial automation solutions", "Machine learning platform", "Edge AI deployment"],
    revenue_model: "Enterprise SaaS / Project-based implementation / Hardware + software bundles",
    operational_structure: "HQ Qingdao China; ~1,000 employees; operations across major Chinese industrial cities",
    distribution_channels: ["Direct enterprise sales", "System integrator partnerships", "Industry-specific verticals"],
    ai_capabilities: "Computer vision for industrial inspection; retail AI analytics; NLP for enterprise workflows; edge AI for on-premise deployment",
  },
  "pacific-union-international": {
    name: "Pacific Union",
    country: "SC", flag: "🇸🇨",
    sector: "Financial Services / Brokerage", sector_category: "Fintech",
    website: "puprime.com",
    valuation_b: null,
    val_trend: null,
    rev_trend: null,
    hc_trend: [150, 200, 250, 300], talent_signal: "Hiring ↑",
    traffic_trend: [0.5, 0.8, 1.2, 1.5],
    linkedin_followers: "10K+",
    glassdoor_rating: 3.8,
    sentiment: "NEUTRAL",
    total_raised_m: null, last_round: "Private",
    strategic_move: "Expanding global retail CFD and forex client base; growing active user base to 1.5M+/mo",
    strategic_tag: "Growth",
    core_offering: "Offshore CFD and forex brokerage (PU Prime) offering multi-asset trading across forex, commodities, indices and crypto to retail clients globally.",
    key_features: ["Multi-asset CFD trading (forex, commodities, indices, crypto)", "MT4/MT5 platform support", "Low-spread retail accounts", "Global client base 150+ countries", "24/5 customer support"],
    revenue_model: "Spread markup / Commission per trade / Swap fees",
    operational_structure: "HQ Seychelles; global operations across Asia, Middle East, Africa, Europe; ~300 employees",
    distribution_channels: ["Web trading platform", "Mobile app", "IB (Introducing Broker) network", "Direct online acquisition"],
    ai_capabilities: "Algorithmic trading support; automated risk management systems; client behavior analytics",
  },
  "abogen-biosciences": {
    name: "Abogen Biosciences",
    country: "CN", flag: "🇨🇳",
    sector: "mRNA Therapeutics", sector_category: "Biotech / Health",
    website: "abogenbio.com",
    valuation_b: 2.0,
    val_trend: [2.0, 2.0, 2.0, 2.0],
    rev_trend: [0, 0, 0, 0],
    hc_trend: [150, 200, 250, 300], talent_signal: "Hiring ↑",
    traffic_trend: null,
    linkedin_followers: "3K",
    glassdoor_rating: null,
    sentiment: "POSITIVE",
    total_raised_m: 700, last_round: "Series C",
    strategic_move: "Advancing mRNA pipeline across infectious disease and oncology; Series C at $2B valuation",
    strategic_tag: "Clinical",
    core_offering: "Clinical-stage Chinese biotech developing mRNA-based vaccines and therapeutics for infectious diseases and oncology, leveraging proprietary lipid nanoparticle delivery technology.",
    key_features: ["Proprietary LNP delivery technology", "mRNA vaccine platform (COVID-19, flu, RSV)", "Oncology mRNA therapeutics pipeline", "In-house GMP manufacturing", "International regulatory track"],
    revenue_model: "Pre-revenue (clinical stage) / Future: milestone payments / royalties / product sales",
    operational_structure: "HQ Suzhou China; R&D and manufacturing in Jiangsu province; ~300 employees",
    distribution_channels: ["Government health procurement", "Hospital and clinic networks", "International licensing deals"],
    ai_capabilities: "AI-assisted mRNA sequence design; computational LNP formulation optimization; clinical trial data analytics",
  },
  "grabwheels": {
    name: "GrabWheels",
    country: "SG", flag: "🇸🇬",
    sector: "Mobility / Tech", sector_category: "Consumer",
    website: "grab.com",
    valuation_b: 15.0,
    val_trend: [14.0, 13.0, 14.5, 15.0],
    rev_trend: [2358, 2800, 3200, 3700],
    hc_trend: [10000, 11000, 11000, 11500], talent_signal: "Stable →",
    traffic_trend: [32, 35, 38, 40],
    linkedin_followers: "1.8M",
    glassdoor_rating: 4.0,
    sentiment: "POSITIVE",
    total_raised_m: 12000, last_round: "NASDAQ: GRAB",
    strategic_move: "Grab Holdings expanding EV and e-scooter fleet across Southeast Asia; revenue +32% YoY 2024",
    strategic_tag: "Expansion",
    core_offering: "Southeast Asia's leading superapp (Grab Holdings) offering ride-hailing, food delivery, payments and logistics — parent company of the GrabWheels e-scooter mobility service.",
    key_features: ["GrabWheels e-scooter sharing service", "GrabCar ride-hailing (8 SEA countries)", "GrabFood delivery platform", "GrabPay digital wallet", "GrabExpress logistics"],
    revenue_model: "Commission per ride/delivery / GrabPay transaction fees / Subscription services / Advertising",
    operational_structure: "HQ Singapore; NASDAQ listed (GRAB); operations in 8 SEA countries; ~11,500 employees",
    distribution_channels: ["Grab superapp (iOS/Android)", "GrabWheels standalone app", "B2B enterprise transport", "API partnerships"],
    ai_capabilities: "AI-powered demand forecasting; dynamic pricing engine; route optimization; fraud detection; driver-partner matching algorithms",
  },
  "aleo-673f": {
    name: "Aleo",
    country: "US", flag: "🇺🇸",
    sector: "Blockchain / ZK Privacy L1", sector_category: "Deep Tech",
    website: "aleo.org",
    valuation_b: 1.45,
    val_trend: [0.38, 0.50, 1.45, 1.45],
    rev_trend: [0, 0, 1, 2],
    hc_trend: [60, 90, 120, 150], talent_signal: "Hiring ↑",
    traffic_trend: [0, 0, 0, 0],
    linkedin_followers: "40K",
    glassdoor_rating: null,
    sentiment: "NEUTRAL",
    total_raised_m: 200, last_round: "Series B",
    strategic_move: "Mainnet launched September 2024; ALEO token live on 20+ exchanges; ZK privacy layer targeting enterprise and DeFi developers",
    strategic_tag: "Mainnet Launch",
    core_offering: "Privacy-focused Layer-1 blockchain using zero-knowledge proofs (ZKPs) to enable fully private, programmable decentralised applications with native on-chain confidentiality.",
    key_features: ["AleoVM — native zero-knowledge virtual machine", "Leo — purpose-built ZK programming language", "ALEO Credits — native token powering transaction fees", "Mainnet live September 2024", "ZK proving system with post-quantum migration challenge"],
    revenue_model: "Network transaction fees (ALEO Credits) / Validator staking ecosystem / Developer grant programme",
    operational_structure: "HQ San Francisco CA; ~150 employees; dual-entity structure (Aleo Systems Inc. + Aleo Network Foundation); mainnet launched September 2024",
    distribution_channels: ["Public blockchain mainnet", "Developer SDK / Leo toolchain", "Grant programme ecosystem", "Enterprise ZK partnerships"],
    ai_capabilities: "Zero-knowledge proof generation (ZK-SNARKs); AleoVM for private verifiable computation; Leo language compiler to ZK circuits; post-quantum cryptography migration research",
  },
};

// Sector filter mapping
export const SECTOR_FILTER_MAP = {
  "AI / Foundation Models": "AI / Tech",
  "AI Search": "AI / Tech",
  "Conversational AI": "AI / Tech",
  "Enterprise AI Search": "AI / Tech",
  "Robotics Software": "AI / Tech",
  "Autonomous Vehicles": "AI / Tech",
  "Social Media / AI": "AI / Tech",
  "AI Data Platform": "AI / Tech",
  "Enterprise AI": "AI / Tech",
  "Legal AI": "AI / Tech",
  "E-Commerce": "E-Commerce",
  "E-Commerce / Marketplace": "E-Commerce",
  "Food Delivery": "E-Commerce",
  "Hospitality Tech": "E-Commerce",
  "On-Demand Storage": "Consumer",
  "Mobility / Tech": "Consumer",
  "AI Drug Discovery": "Biotech / Health",
  "mRNA Therapeutics": "Biotech / Health",
  "Liquid Biopsy / Oncology": "Biotech / Health",
  "Cell Therapy / Oncology": "Biotech / Health",
  "Genomics Technology": "Biotech / Health",
  "Medical AI / Endoscopy": "Biotech / Health",
  "GLP-1 / Obesity Biotech": "Biotech / Health",
  "Fusion Energy": "Deep Tech",
  "Quantum Computing": "Deep Tech",
  "Semiconductor IP": "Deep Tech",
  "Defense / AI Tech": "Deep Tech",
  "Blockchain / ZK Privacy L1": "Deep Tech",
  "Telematics / InsurTech": "Fintech",
  "Working Capital / Fintech": "Fintech",
  "Fintech / SEA": "Fintech",
  "Payments / Fintech": "Fintech",
  "Financial Services / Brokerage": "Fintech",
  "Corporate Fintech": "Fintech",
  "Corporate Travel SaaS": "SaaS / B2B",
  "Enterprise RPA / AI": "SaaS / B2B",
  "B2B Revenue Intelligence": "SaaS / B2B",
  "Security Automation": "SaaS / B2B",
  "Marketing Data Platform": "SaaS / B2B",
  "Cybersecurity / Cloud": "SaaS / B2B",
  "Learning Management SaaS": "SaaS / B2B",
  "Gaming Backend SaaS": "SaaS / B2B",
  "Design / SaaS": "SaaS / B2B",
  "Data / AI Platform": "SaaS / B2B",
  "HR / Finance SaaS": "SaaS / B2B",
  "Design SaaS": "SaaS / B2B",
  "Productivity SaaS": "SaaS / B2B",
  "Vertical Farming": "Consumer",
  "Flexible Workspaces": "Consumer",
  "Social AI / Entertainment": "Consumer",
};

export const SECTOR_CATEGORIES = [
  "All", "AI / Tech", "E-Commerce", "Biotech / Health",
  "Fintech", "Deep Tech", "SaaS / B2B", "Consumer",
];
